import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute, Data } from '@angular/router';
import { filter, map } from 'rxjs/operators';

import { ModernFramePresentationService } from '../modern-host/modern-frame-presentation.service';

@Injectable({
  providedIn: 'root'
})
export class PageTitleService {
  private readonly baseTitle = 'Performance Portal';
  private readonly modernPresentation = inject(ModernFramePresentationService);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title
  ) {
    this.init();
  }

  /** Re-applies the shell route title after the modern overlay deactivates. */
  refreshFromCurrentRoute(): void {
    this.applyTitle(this.getRouteTitle(this.activatedRoute));
  }

  private applyTitle(title: string | null): void {
    if (title) {
      this.titleService.setTitle(title);
    } else {
      this.titleService.setTitle(this.baseTitle);
    }
  }

  /**
   * Iterates through the active route hierarchy (from the deepest child to the parent)
   * to find the first 'breadcrumb' definition that is a valid string.
   * This resolves the issue of child routes using { skip: true } resulting in [object Object].
   */
  private getRouteTitle(route: ActivatedRoute): string | null {
    // 1. Finds the leaf route (the deepest in the hierarchy)
    let currentRoute: ActivatedRoute | null = route;
    while (currentRoute && currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    // 2. Iterates back up the hierarchy (from child to parent)
    while (currentRoute) {
      const data: Data = currentRoute.snapshot.data;
      const breadcrumb = data['breadcrumb']; // If it's a valid string, we found our title.

      if (typeof breadcrumb === 'string' && breadcrumb.length > 0) {
        return breadcrumb;
      } // If it is an object with the 'skip: true' property, continue searching the parent.

      if (typeof breadcrumb === 'object' && breadcrumb !== null && breadcrumb['skip'] === true) {
        currentRoute = currentRoute.parent;
        continue;
      }

      // If it is an object that is NOT { skip: true } or any other invalid type,
      // and not a string, we just move up the hierarchy to the parent
      // to ensure we don't use 'object Object'.
      currentRoute = currentRoute.parent;
    }

    return null; // Did not find a valid title
  }

  init(): void {
    this.router.events
      .pipe(
        // Filters only for completed navigation events
        filter((event): event is NavigationEnd => event instanceof NavigationEnd), // Maps to the title found in the route hierarchy
        map(() => this.getRouteTitle(this.activatedRoute))
      )
      .subscribe((title) => {
        if (this.modernPresentation.isModernActive()) return;
        this.applyTitle(title);
      });
  }
}
